import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import type {
  FormManagementGeneratedLink,
  FormManagementWorkspaceData,
} from '@/contracts/form-management'
import { Copy, FileDown, Link2, ShieldAlert, UploadCloud } from 'lucide-react'
import { useMemo, useState } from 'react'

type FormManagementLinkGeneratorProps = {
  workspaceData: FormManagementWorkspaceData
}

const statusLabels: Record<FormManagementGeneratedLink['status'], string> = {
  ready: '可預覽',
  mock_only: 'Mock only',
  provider_not_configured: 'Provider 未接線',
}

const statusVariants: Record<FormManagementGeneratedLink['status'], 'success' | 'warning' | 'secondary'> = {
  ready: 'success',
  mock_only: 'secondary',
  provider_not_configured: 'warning',
}

export function FormManagementLinkGenerator({ workspaceData }: FormManagementLinkGeneratorProps) {
  const [batchName, setBatchName] = useState('五月 B2B 建檔邀請')
  const [salesCode, setSalesCode] = useState('S-104')
  const [campaignCode, setCampaignCode] = useState('MAY-A')
  const [utmSource, setUtmSource] = useState('newsletter')
  const [utmCampaign, setUtmCampaign] = useState('may-onboarding')

  const generatedPreviewUrl = useMemo(() => {
    const url = new URL(workspaceData.linkGenerator.previewUrl)
    url.searchParams.set('utm_source', utmSource)
    url.searchParams.set('utm_campaign', utmCampaign)
    url.searchParams.set('salesCode', salesCode)
    url.searchParams.set('campaignCode', campaignCode)
    return url.toString()
  }, [
    campaignCode,
    salesCode,
    utmCampaign,
    utmSource,
    workspaceData.linkGenerator.previewUrl,
  ])

  const templateText = workspaceData.linkGenerator.templateColumns.join(',')

  function copyPreviewUrl() {
    void navigator.clipboard?.writeText(generatedPreviewUrl)
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge>Mock-safe</Badge>
        <Badge variant="secondary">contract preview</Badge>
        <Badge variant="outline">tracking URL contract preview</Badge>
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Link2 className="size-5 text-indigo-600" />
                <h3 className="font-semibold text-slate-950">網址產生器</h3>
              </div>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
                批次產生帶有 UTM、hidden fields、業務代碼與活動代碼的問卷網址。
                此頁只展示前端 contract 與 synthetic preview，不讀取上傳檔、不建立真實 tracking link。
              </p>
            </div>
            <Button type="button" variant="outline" disabled>
              <FileDown className="size-4" />
              範本預覽未接線
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-semibold text-slate-950">批次範本與上傳區</h3>
              <Badge variant="warning">{workspaceData.linkGenerator.uploadState}</Badge>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
              <div>
                <label className="text-sm font-medium text-slate-700">CSV 欄位 contract</label>
                <Textarea
                  className="mt-2 min-h-28 font-mono text-xs"
                  readOnly
                  value={`${templateText}\nAcme Co.,buyer@example.com,${salesCode},${campaignCode},${utmSource},email,${utmCampaign}`}
                />
                <p className="mt-2 text-xs leading-5 text-slate-500">
                  後端接線前不讀取或下載真檔案；欄位只作為 API contract 與 UI 驗收用。
                </p>
              </div>

              <div className="grid place-items-center rounded-lg border border-dashed border-slate-300 bg-slate-50 p-5 text-center">
                <div>
                  <UploadCloud className="mx-auto size-8 text-slate-400" />
                  <div className="mt-3 text-sm font-medium text-slate-800">上傳名單</div>
                  <p className="mt-2 text-xs leading-5 text-slate-500">
                    Mock UI only。production 需先完成檔案安全、PII 與 provider 審查。
                  </p>
                  <Button type="button" className="mt-4" variant="secondary" disabled>
                    選擇檔案
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <ShieldAlert className="mt-0.5 size-5 text-amber-500" />
              <div>
                <h3 className="font-semibold text-slate-950">狀態與限制</h3>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  網址產生器目前不寫入 DB、不建立短網址、不觸發 Email。後續要由後端提供
                  tracking_links、hidden_fields 與 audit log。
                </p>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {workspaceData.linkGenerator.parameters.map((parameter) => (
                <div key={parameter.id} className="rounded-md bg-slate-50 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs text-slate-700">{parameter.label}</span>
                    <Badge variant="secondary">{parameter.value}</Badge>
                  </div>
                  <p className="mt-2 text-xs leading-5 text-slate-500">{parameter.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="grid gap-4 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <label className="text-sm font-medium text-slate-700">批次名稱</label>
              <Input className="mt-2" value={batchName} onChange={(event) => setBatchName(event.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">業務代碼</label>
              <Input className="mt-2" value={salesCode} onChange={(event) => setSalesCode(event.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">活動代碼</label>
              <Input
                className="mt-2"
                value={campaignCode}
                onChange={(event) => setCampaignCode(event.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">utm_source</label>
              <Input className="mt-2" value={utmSource} onChange={(event) => setUtmSource(event.target.value)} />
            </div>
            <div className="lg:col-span-2">
              <label className="text-sm font-medium text-slate-700">utm_campaign</label>
              <Input
                className="mt-2"
                value={utmCampaign}
                onChange={(event) => setUtmCampaign(event.target.value)}
              />
            </div>
            <div className="lg:col-span-3">
              <label className="text-sm font-medium text-slate-700">Tracking URL contract preview</label>
              <div className="mt-2 flex min-h-10 items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <code className="min-w-0 flex-1 break-all text-xs text-slate-700">{generatedPreviewUrl}</code>
                <Button type="button" size="sm" variant="outline" onClick={copyPreviewUrl}>
                  <Copy className="size-4" />
                  複製
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-md bg-indigo-50 p-3 text-sm leading-6 text-indigo-800">
            「{batchName || '未命名批次'}」會在後端接線後建立 tracking link job；目前只顯示 URL
            組成與 hidden fields 契約。
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-semibold text-slate-950">產生紀錄</h3>
            <Badge variant="outline">{workspaceData.linkGenerator.generatedLinks.length} 筆 synthetic</Badge>
          </div>
          <div className="mt-4 overflow-hidden rounded-md border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>名稱</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>數量</TableHead>
                  <TableHead>狀態</TableHead>
                  <TableHead>建立時間</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workspaceData.linkGenerator.generatedLinks.map((link) => (
                  <TableRow key={link.id}>
                    <TableCell className="font-medium text-slate-900">{link.name}</TableCell>
                    <TableCell className="max-w-md break-all font-mono text-xs text-slate-600">
                      {link.url}
                    </TableCell>
                    <TableCell>{link.count}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariants[link.status]}>{statusLabels[link.status]}</Badge>
                    </TableCell>
                    <TableCell>{link.createdAt}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
